/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

interface SortableHeaderProps {
  /** Column key for sorting */
  columnKey: string;
  /** Display label */
  label: string;
  /** Current sort configuration */
  sortConfig: SortConfig | null;
  /** Callback when sort is requested */
  onSort: (key: string) => void;
  /** Additional CSS classes */
  className?: string;
  /** Whether sorting is disabled */
  disabled?: boolean;
  /** Alignment */
  align?: 'left' | 'center' | 'right';
}

/**
 * SortableHeader - Futuristic sortable column header component
 *
 * Features:
 * - Smooth animations on hover and click
 * - Visual sort direction indicators
 * - Glow effect on active sort
 * - Keyboard accessible
 */
export function SortableHeader({
  columnKey,
  label,
  sortConfig,
  onSort,
  className = '',
  disabled = false,
  align = 'left',
}: SortableHeaderProps) {
  const isActive = sortConfig?.key === columnKey;
  const direction = isActive ? sortConfig.direction : null;

  const handleClick = () => {
    if (!disabled) {
      onSort(columnKey);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      onSort(columnKey);
    }
  };

  const alignClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }[align];

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          sortable-header
          ${isActive ? 'sortable-header--active' : ''}
          ${direction === 'asc' ? 'sortable-header--asc' : ''}
          ${direction === 'desc' ? 'sortable-header--desc' : ''}
          ${disabled ? 'sortable-header--disabled' : ''}
          ${alignClass}
          ${className}
        `}
        aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
      >
        <span className="sortable-header__label">{label}</span>
        <span className="sortable-header__icon">
          {direction === 'asc' ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : direction === 'desc' ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronsUpDown className="w-3.5 h-3.5" />
          )}
        </span>
      </button>
      <style>{sortableHeaderStyles}</style>
    </>
  );
}

/**
 * Hook to manage sort state
 */
export function useSort(defaultKey?: string, defaultDirection: SortDirection = null) {
  const [sortConfig, setSortConfig] = React.useState<SortConfig | null>(
    defaultKey ? { key: defaultKey, direction: defaultDirection } : null
  );

  const handleSort = React.useCallback((key: string) => {
    setSortConfig((current) => {
      if (current?.key !== key) {
        // New column - start with ascending
        return { key, direction: 'asc' };
      }
      // Same column - cycle: asc -> desc -> null
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (current.direction === 'desc') {
        return null;
      }
      return { key, direction: 'asc' };
    });
  }, []);

  // Get sort parameter for API (e.g., "name,asc" or "createdAt,desc")
  const getSortParam = React.useCallback(() => {
    if (!sortConfig || !sortConfig.direction) return undefined;
    return `${sortConfig.key},${sortConfig.direction}`;
  }, [sortConfig]);

  return { sortConfig, handleSort, getSortParam };
}

const sortableHeaderStyles = `
  .sortable-header {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    margin: -4px -8px;
    border: none;
    background: transparent;
    font-size: inherit;
    font-weight: 600;
    color: var(--color-secondary, #94a3b8);
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    white-space: nowrap;
    user-select: none;
  }

  .sortable-header::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg,
      rgba(59, 130, 246, 0.1) 0%,
      rgba(147, 51, 234, 0.1) 100%
    );
    opacity: 0;
    transition: opacity 0.2s ease;
    border-radius: inherit;
  }

  .sortable-header:hover:not(:disabled) {
    color: var(--color-foreground, #f8fafc);
  }

  .sortable-header:hover:not(:disabled)::before {
    opacity: 1;
  }

  .sortable-header:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }

  .sortable-header__label {
    position: relative;
    z-index: 1;
  }

  .sortable-header__icon {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.4;
    transition: all 0.2s ease;
  }

  .sortable-header:hover:not(:disabled) .sortable-header__icon {
    opacity: 0.8;
    transform: scale(1.1);
  }

  /* Active state - when sorting by this column */
  .sortable-header--active {
    color: var(--color-foreground, #f8fafc);
  }

  .sortable-header--active::before {
    opacity: 1;
    background: linear-gradient(135deg,
      rgba(59, 130, 246, 0.15) 0%,
      rgba(147, 51, 234, 0.15) 100%
    );
  }

  .sortable-header--active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60%;
    height: 2px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(59, 130, 246, 0.8) 50%,
      transparent 100%
    );
    border-radius: 1px;
  }

  .sortable-header--active .sortable-header__icon {
    opacity: 1;
    color: #3b82f6;
  }

  /* Ascending animation */
  .sortable-header--asc .sortable-header__icon {
    animation: sortBounceUp 0.3s ease;
  }

  /* Descending animation */
  .sortable-header--desc .sortable-header__icon {
    animation: sortBounceDown 0.3s ease;
  }

  /* Disabled state */
  .sortable-header--disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  @keyframes sortBounceUp {
    0% { transform: translateY(3px); opacity: 0; }
    50% { transform: translateY(-2px); }
    100% { transform: translateY(0); opacity: 1; }
  }

  @keyframes sortBounceDown {
    0% { transform: translateY(-3px); opacity: 0; }
    50% { transform: translateY(2px); }
    100% { transform: translateY(0); opacity: 1; }
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .sortable-header {
      padding: 2px 4px;
      margin: -2px -4px;
      gap: 4px;
    }

    .sortable-header__icon {
      display: none;
    }

    .sortable-header--active .sortable-header__icon {
      display: flex;
    }
  }
`;

export default SortableHeader;
